import { supabase } from './supabase'
import { offlineDB } from './offlineDB'

class SyncService {
  constructor() {
    this.isSyncing = false
    this.syncCallbacks = []
  }

  onSyncComplete(callback) {
    this.syncCallbacks.push(callback)
  }

  async syncAll() {
    if (this.isSyncing) {
      console.log('Sincronización ya en curso...')
      return { success: false, message: 'Ya hay una sincronización en curso' }
    }

    this.isSyncing = true
    console.log('🔄 Iniciando sincronización completa...')

    try {
      let totalSynced = 0

      // 1. Sincronizar operaciones pendientes (clientes, productos, plantillas)
      const pendingOps = await offlineDB.getPendingOperations()
      console.log(`📋 ${pendingOps.length} operaciones pendientes`)

      for (const op of pendingOps) {
        try {
          if (op.type === 'client') {
            await this.syncClientOperation(op)
          } else if (op.type === 'product') {
            await this.syncProductOperation(op)
          } else if (op.type === 'template') {
            await this.syncTemplateOperation(op)
          } else if (op.type === 'delivery') {
            await this.syncDeliveryOperation(op)
          }
          
          await offlineDB.markOperationSynced(op.id)
          totalSynced++
          console.log(`✅ Operación ${totalSynced}/${pendingOps.length} sincronizada`)
        } catch (error) {
          console.error(`❌ Error sincronizando operación ${op.type}:`, error)
          // Continuar con la siguiente operación
        }
      }

      // 2. Sincronizar albaranes offline que no están en operaciones pendientes
      const deliveriesOffline = await offlineDB.getDeliveriesOffline()
      const deliveriesNoSincronizados = deliveriesOffline.filter(d => !d.synced)
      console.log(`📦 ${deliveriesNoSincronizados.length} albaranes adicionales pendientes`)

      for (const delivery of deliveriesNoSincronizados) {
        try {
          // Solo sincronizar si es INSERT (no tiene ID temporal o no está en operaciones)
          if (!delivery.id.toString().startsWith('temp_')) {
            console.log('⏭️ Saltando albarán con ID real:', delivery.id)
            continue
          }

          const { data: newDelivery, error: deliveryError } = await supabase
            .from('deliveries')
            .insert({
              client_id: delivery.client_id,
              fecha: delivery.fecha,
              ruta: delivery.ruta,
              estado: 'entregado'
            })
            .select('id')
            .single()

          if (deliveryError) throw deliveryError

          if (delivery.lines && delivery.lines.length > 0) {
            const lineasParaInsertar = delivery.lines.map(l => ({
              delivery_id: newDelivery.id,
              product_id: l.product_id,
              cantidad_entregada: l.cantidad_entregada,
              cantidad_devuelta: l.cantidad_devuelta || 0,
              precio_unit: l.precio_unit,
              iva_pct: l.iva_pct,
              descuento_aplicable: l.descuento_aplicable || false
            }))

            const { error: linesError } = await supabase
              .from('delivery_lines')
              .insert(lineasParaInsertar)

            if (linesError) throw linesError
          }

          await offlineDB.markDeliverySynced(delivery.id)
          totalSynced++
          console.log(`✅ Albarán ${totalSynced} sincronizado`)
        } catch (error) {
          console.error('❌ Error sincronizando albarán:', error)
        }
      }

      // 3. Limpiar operaciones ya sincronizadas
      await offlineDB.clearSyncedOperations()

      // 4. Actualizar caché con datos frescos de Supabase
      await this.updateCache()

      // 5. ✅ ACTUALIZAR SINCRONIZACIÓN DEL USUARIO (CRÍTICO)
      try {
        const currentUserStr = localStorage.getItem('currentUser')
        if (currentUserStr) {
          const currentUser = JSON.parse(currentUserStr)
          if (['Ruta 1', 'Ruta 2', 'Movil'].includes(currentUser.username)) {
            const { error: syncError } = await supabase.rpc('actualizar_sincronizacion', { 
              user_name: currentUser.username 
            })
            
            if (syncError) {
              console.error('Error actualizando sincronización:', syncError)
            } else {
              console.log(`✅ Sincronización actualizada para ${currentUser.username}`)
            }
          }
        }
      } catch (e) {
        console.error('Error en actualización de sincronización:', e)
      }

      console.log(`✅ Sincronización completa: ${totalSynced} elementos sincronizados`)

      this.syncCallbacks.forEach(cb => cb({ success: true, count: totalSynced }))

      return { success: true, count: totalSynced }
    } catch (error) {
      console.error('❌ Error en sincronización:', error)
      this.syncCallbacks.forEach(cb => cb({ success: false, error }))
      return { success: false, error }
    } finally {
      this.isSyncing = false
    }
  }

  async syncClientOperation(op) {
    const { operation, data } = op

    if (operation === 'insert') {
      const { error } = await supabase
        .from('clients')
        .insert(data)
      if (error) throw error
    } else if (operation === 'update') {
      const { error } = await supabase
        .from('clients')
        .update(data)
        .eq('id', data.id)
      if (error) throw error
    } else if (operation === 'delete') {
      const { error } = await supabase
        .from('clients')
        .delete()
        .eq('id', data.id)
      if (error) throw error
    }
  }

  async syncProductOperation(op) {
    const { operation, data } = op

    if (operation === 'insert') {
      const { error } = await supabase
        .from('products')
        .insert(data)
      if (error) throw error
    } else if (operation === 'update') {
      const { error } = await supabase
        .from('products')
        .update(data)
        .eq('id', data.id)
      if (error) throw error
    } else if (operation === 'delete') {
      const { error } = await supabase
        .from('products')
        .delete()
        .eq('id', data.id)
      if (error) throw error
    }
  }

  async syncTemplateOperation(op) {
    const { operation, data } = op

    if (operation === 'delete_all') {
      await supabase
        .from('client_templates')
        .delete()
        .eq('client_id', data.client_id)
    } else if (operation === 'insert_batch') {
      if (data.templates && data.templates.length > 0) {
        const { error } = await supabase
          .from('client_templates')
          .insert(data.templates)
        if (error) throw error
      }
    }
  }

  async syncDeliveryOperation(op) {
    const { operation, data } = op

    if (operation === 'update') {
      // ========== ACTUALIZAR ALBARÁN EXISTENTE ==========
      console.log('🔄 Sincronizando UPDATE de albarán:', data.id)
      
      // Solo sincronizar si NO es ID temporal
      if (data.id.toString().startsWith('temp_')) {
        console.log('⏭️ Saltando UPDATE de ID temporal')
        return
      }
      
      // Eliminar líneas antiguas
      await supabase
        .from('delivery_lines')
        .delete()
        .eq('delivery_id', data.id)
      
      // Insertar nuevas líneas
      if (data.lines && data.lines.length > 0) {
        const lineasParaInsertar = data.lines.map(l => ({
          delivery_id: data.id,
          product_id: l.product_id,
          cantidad_entregada: l.cantidad_entregada,
          cantidad_devuelta: l.cantidad_devuelta || 0,
          precio_unit: l.precio_unit,
          iva_pct: l.iva_pct,
          descuento_aplicable: l.descuento_aplicable || false
        }))

        const { error } = await supabase
          .from('delivery_lines')
          .insert(lineasParaInsertar)
        
        if (error) throw error
      }
    } else if (operation === 'insert') {
      // ========== CREAR ALBARÁN NUEVO ==========
      console.log('✨ Sincronizando INSERT de albarán')
      
      const { data: newDelivery, error: deliveryError } = await supabase
        .from('deliveries')
        .insert({
          client_id: data.client_id,
          fecha: data.fecha,
          ruta: data.ruta,
          estado: data.estado || 'entregado'
        })
        .select('id')
        .single()

      if (deliveryError) throw deliveryError

      if (data.lines && data.lines.length > 0) {
        const lineasParaInsertar = data.lines.map(l => ({
          delivery_id: newDelivery.id,
          product_id: l.product_id,
          cantidad_entregada: l.cantidad_entregada,
          cantidad_devuelta: l.cantidad_devuelta || 0,
          precio_unit: l.precio_unit,
          iva_pct: l.iva_pct,
          descuento_aplicable: l.descuento_aplicable || false
        }))

        const { error: linesError } = await supabase
          .from('delivery_lines')
          .insert(lineasParaInsertar)

        if (linesError) throw linesError
      }

      // Actualizar ID temporal por ID real en caché local
      if (data.id.toString().startsWith('temp_')) {
        await offlineDB.delete('deliveries_offline', data.id)
        const deliveryActualizado = {
          ...data,
          id: newDelivery.id,
          synced: true
        }
        await offlineDB.put('deliveries_offline', deliveryActualizado)
      }
    }
  }

  async updateCache() {
    console.log('📥 Actualizando caché...')

    try {
      const { data: clients } = await supabase
        .from('clients')
        .select('*')
        .eq('activo', true)

      if (clients) {
        await offlineDB.cacheClients(clients)
        console.log(`✅ ${clients.length} clientes en caché`)
      }

      const { data: products } = await supabase
        .from('products')
        .select('*')
        .eq('activo', true)

      if (products) {
        await offlineDB.cacheProducts(products)
        console.log(`✅ ${products.length} productos en caché`)
      }

      const { data: templates } = await supabase
        .from('client_templates')
        .select('*')
        .eq('activo', true)

      if (templates) {
        await offlineDB.cacheTemplates(templates)
        console.log(`✅ ${templates.length} plantillas en caché`)
      }

      const { data: users } = await supabase
        .from('users')
        .select('*')

      if (users) {
        await offlineDB.cacheUsers(users)
        console.log(`✅ ${users.length} usuarios en caché`)
      }

      // ── Cachear historial de albaranes (últimos 14 días) para cobro offline del lunes ──
      try {
        const fechaCorte = new Date()
        fechaCorte.setDate(fechaCorte.getDate() - 14)
        const fechaDesdeHistorial = fechaCorte.toISOString().slice(0, 10)

        const { data: histDeliveries } = await supabase
          .from('deliveries')
          .select(`
            id, client_id, fecha, ruta, estado,
            delivery_lines (
              id, product_id, cantidad_entregada, cantidad_devuelta,
              precio_unit, iva_pct, descuento_aplicable
            )
          `)
          .gte('fecha', fechaDesdeHistorial)
          .eq('estado', 'entregado')

        if (histDeliveries && histDeliveries.length > 0) {
          await offlineDB.cacheDeliveriesHistory(histDeliveries)
          console.log(`✅ ${histDeliveries.length} albaranes históricos en caché (desde ${fechaDesdeHistorial})`)
        }

        // Limpiar entradas con más de 21 días
        await offlineDB.clearOldDeliveriesHistory(21)
      } catch (histError) {
        console.warn('⚠️ No se pudo cachear historial de albaranes:', histError)
      }

      return true
    } catch (error) {
      console.error('Error actualizando caché:', error)
      return false
    }
  }
}

export const syncService = new SyncService()